/**
 * The account page every signed-in user gets — member and superadmin alike.
 *
 * Nothing on it is admin-gated, and that is the point: until now a user could
 * neither clear `must_change_password` nor turn 2FA on without curl. The two
 * things a person can actually do to their own security posture live here, and
 * both of them are self-service by design — the server checks the current
 * password before it accepts a new one, and it checks a live TOTP code before
 * it believes an authenticator exists.
 *
 * The page owns no session state. It reads the user from `useAuth()` and, after
 * either action lands, re-reads `/auth/me` rather than patching its own copy:
 * `must_change_password` and `totp_enabled` are the server's answers, and a
 * screen that edits them locally is a screen that can disagree with the backend.
 */
import { useState } from 'react'

import { useAuth } from '../auth/useAuth'
import { useI18n } from '../i18n'
import { AccountSummary } from './AccountSummary'
import { ChangePasswordForm } from './ChangePasswordForm'
import { TwoFactorSetup } from './TwoFactorSetup'

export function ProfilePage() {
  const { user, reload } = useAuth()
  /**
   * The server's own `must_change_password: false`, remembered.
   *
   * `reload()` is still the source of truth for everything else, but it can
   * fail: `useAuth` deliberately KEEPS the previous user on any non-401 error,
   * so a blip on the refetch would leave a flag the server has already cleared
   * set in memory — banner up, success message underneath it, and the rotation
   * gate bouncing the user back here on every nav click, with no way out of
   * the page short of reloading the tab.
   */
  const [rotationCleared, setRotationCleared] = useState(false)

  // Rendered under <RequireAuth/>, so this is the brief gap before `/auth/me`
  // answers on a cold load, not an anonymous visitor.
  if (!user) return null

  // No header and no <main> of its own: `<AppShell/>` (nav/AppHeader.tsx) wraps
  // this route and owns both, so a layout here would put a second bar on the page.
  return (
    <>
      {user.must_change_password && !rotationCleared && <MustChangePasswordBanner />}

      <AccountSummary user={user} />

      <ChangePasswordForm
        onChanged={(result) => {
          if (!result.must_change_password) setRotationCleared(true)
          void reload()
        }}
      />

      {/* `required` is the server's own `users.totp_required`, not a guess. An
          account under the mandate can be sitting on this page right now -- a
          superadmin `disable-2fa` leaves the owner signed in -- and offering
          the setup as a nicety would hide that their next sign-in is blocked
          until it is done. `onAcknowledge` gives the once-only backup codes the
          same explicit dismissal they get on the invite path. */}
      <TwoFactorSetup
        enabled={user.totp_enabled}
        required={user.totp_required}
        onEnabled={() => void reload()}
        onAcknowledge={() => void reload()}
      />
    </>
  )
}

/**
 * The flag that used to be a dead end.
 *
 * It is set on every invited account and on the bootstrap superadmin, and
 * `POST /auth/password` is the only thing in the system that clears it — so the
 * banner points at the form directly below it and disappears the moment the
 * reloaded `/auth/me` says the change landed.
 */
function MustChangePasswordBanner() {
  const { t } = useI18n()
  return (
    <div
      data-testid="must-change-password"
      role="alert"
      className="space-y-1 rounded-lg border border-degraded/40 bg-degraded/10 p-4"
    >
      <p className="text-sm font-semibold text-degraded">{t('account.profile.rotationTitle')}</p>
      <p className="text-sm text-fg">{t('account.profile.rotationBody')}</p>
    </div>
  )
}

export default ProfilePage
